import React from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken } from '@vibesites/runtime/react';
import { usePropertyField, useProperty } from '@vibesites/runtime/react';
import { EditableToken, useBlockSlot, SectionContextProvider } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';

// ============================================================================
// Helpers
// ============================================================================

function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (/am|pm/i.test(trimmed)) return trimmed;
  const [h, m] = trimmed.split(':').map(Number);
  if (isNaN(h)) return trimmed;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, '0')} ${suffix}` : `${hr} ${suffix}`;
}

interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

// ============================================================================
// Component
// ============================================================================

export function ContactForm() {
  const phoneOverride = useContentToken('contactform.phone', { defaultValue: '' });
  const phoneFallback = usePropertyField('property_phone') as string | null;
  const phone = phoneOverride || phoneFallback || '(555) 555-5555';
  const address = useContentToken('contactform.address');
  const city = usePropertyField('property_address_city') as string || '';
  const state = usePropertyField('property_address_state') as string || '';
  const zip = usePropertyField('property_address_zip') as string || '';
  const cityStateZip = [city, state].filter(Boolean).join(', ') + (zip ? `, ${zip}` : '');

  const property = useProperty();
  const officeHours = (property?.office_hours as OfficeHourEntry[] | null) || [];

  const formSlot = useBlockSlot('contactform.form_block', {
    defaultValue: 'multihub-lead-form',
  });

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  const hasAddress = !!address || !!cityStateZip;
  const hasPhone = !!phone;
  const hasHours = officeHours.length > 0;

  return (
    <section className="cf-section">
      <div ref={sectionRef} className="reveal-fade-up cf-container">
        <div className="cf-grid">
          {/* Left column: contact info */}
          <div className="cf-info-col">
            <div className="cf-info-cards">
              {hasAddress && (
                <div className="cf-card">
                  <div className="cf-card-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1C6.13 1 3 4.13 3 8c0 5.25 7 11 7 11s7-5.75 7-11c0-3.87-3.13-7-7-7zm0 9.5A2.5 2.5 0 1110 5.5a2.5 2.5 0 010 5z" fill="currentColor"/></svg>
                  </div>
                  <div>
                    <h3 className="cf-card-label">Visit Us</h3>
                    <EditableToken path="contactform.address" defaultField="property_address" defaultValue="123 Main Street" as="p" className="cf-card-value">
                      {address}
                    </EditableToken>
                    {cityStateZip && <p className="cf-card-value">{cityStateZip}</p>}
                  </div>
                </div>
              )}

              {hasPhone && (
                <div className="cf-card">
                  <div className="cf-card-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4 2a2 2 0 00-2 2v1a13 13 0 0013 13h1a2 2 0 002-2v-2.5a1 1 0 00-.68-.95l-3.12-1a1 1 0 00-1.02.27l-1.38 1.38A11 11 0 015.8 7.2l1.38-1.38a1 1 0 00.27-1.02l-1-3.12A1 1 0 005.5 1H4z" fill="currentColor"/></svg>
                  </div>
                  <div>
                    <h3 className="cf-card-label">Call Us</h3>
                    <EditableToken path="contactform.phone" type="phone" label="Phone">
                      <a href={`tel:${phone?.replace(/[^+\d]/g, '')}`} type="call" className="dniphone dniphonehref cf-card-value cf-card-link">
                        {formatDisplayPhone(phone)}
                      </a>
                    </EditableToken>
                  </div>
                </div>
              )}

              {hasHours && (
                <div className="cf-card">
                  <div className="cf-card-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 0a10 10 0 100 20 10 10 0 000-20zm0 18a8 8 0 110-16 8 8 0 010 16zm.5-13H9v6l5.25 3.15.75-1.23-4.5-2.67V5z" fill="currentColor"/></svg>
                  </div>
                  <div>
                    <h3 className="cf-card-label">Office Hours</h3>
                    <div className="cf-hours-grid">
                      {officeHours.map((h, i) => (
                        <div key={i} className="cf-hours-row">
                          <span className="cf-hours-day">{h.day}</span>
                          <span className="cf-hours-time">
                            {h.open_time && h.close_time
                              ? `${formatTime(h.open_time)} – ${formatTime(h.close_time)}`
                              : 'Closed'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right column: block slot (defaults to multihub lead form) */}
          <div className="cf-form-col">
            {formSlot ? (
              <EditableToken path="contactform.form_block" type="block" label="Form Block">
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              </EditableToken>
            ) : (
              <div className="cf-form-card">
                <p className="cf-form-subtitle">No form block selected.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .cf-section {
          padding: 2.5rem 0 3.5rem;
          background: white;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 3rem 0 4rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .cf-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .cf-container { padding: 0 2rem; } }

        .cf-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }
        @media (min-width: 1024px) {
          .cf-grid {
            grid-template-columns: 5fr 7fr;
            gap: 2.5rem;
            align-items: start;
          }
        }

        /* ===== Info column ===== */
        .cf-info-col {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .cf-info-cards {
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          overflow: hidden;
        }
        .cf-card {
          display: flex;
          align-items: flex-start;
          gap: 0.875rem;
          padding: 1.25rem 1.5rem;
          border-bottom: 1px solid var(--vibe-color-border, #e5e7eb);
        }
        .cf-card:last-child { border-bottom: none; }
        .cf-card-icon {
          flex-shrink: 0;
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          background: color-mix(in srgb, var(--vibe-color-primary) 10%, transparent);
          color: var(--vibe-color-primary);
          margin-top: 1px;
        }
        .cf-card-label {
          font-family: var(--vibe-font-heading);
          font-size: 0.7rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--vibe-color-primary);
          margin: 0 0 0.25rem;
        }
        .cf-card-value {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .cf-card-link {
          text-decoration: none;
          color: var(--vibe-color-text);
          transition: color 0.2s;
        }
        .cf-card-link:hover { color: var(--vibe-color-primary); }

        .cf-hours-grid {
          display: flex;
          flex-direction: column;
          gap: 0.2rem;
          margin-top: 0.125rem;
        }
        .cf-hours-row {
          display: flex;
          justify-content: space-between;
          gap: 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.825rem;
          line-height: 1.6;
        }
        .cf-hours-day {
          color: var(--vibe-color-text);
          font-weight: 500;
          min-width: 5.5rem;
        }
        .cf-hours-time {
          color: var(--vibe-color-text-muted);
          text-align: right;
        }

        /* ===== Form column ===== */
        .cf-form-col { position: relative; }
        .cf-form-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          border: 1px solid var(--vibe-color-border, #e5e7eb);
          padding: 2rem;
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.04);
        }
        @media (min-width: 768px) {
          .cf-form-card { padding: 2.5rem; }
        }
        .cf-form-subtitle {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
          line-height: 1.5;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactForm',
  displayName: 'Contact Form',
  tokens: {
    'contactform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'contactform.address': {
      label: 'Address',
      type: 'text',
      defaultField: 'property_address',
      defaultValue: '123 Main Street',
    },
    'contactform.form_block': {
      label: 'Form Block',
      type: 'block',
      description: 'Block to render in the form area',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ContactForm as any).contentSchema = contentSchema;

export default ContactForm;
